/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1724889600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Aug 29 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "marigold-engage",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported content",
          "depth": 0,
          "id": "supported-content"
        }, {
          "value": "Connect to Marigold Engage",
          "depth": 0,
          "id": "connect-to-marigold-engage"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-content",
        children: "Supported content"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Users"
              })
            }), _jsx(_components.td, {
              children: "Sync records from your model to lists and segments"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-marigold-engage",
        children: "Connect to Marigold Engage"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Marigold Engage"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Marigold Engage"
        }), " via a Service Account."]
      }), "\n", _jsxs(_components.p, {
        children: ["To create a Service Account, click the settings gear in the bottom left corner of the Marigold Engage dashboard. Then, navigate to ", _jsx(_components.strong, {
          children: "Access Management > Service Accounts"
        }), " and click ", _jsx(_components.strong, {
          children: "New"
        }), ". Make sure to set an expiration date and configure the following endpoints:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists/{list}/fields"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists/{list}/data/search"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists/{list}/data/load"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists/{list}/segments"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists/{list}/segments/{segment}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "/organizations/{organization}/lists/{list}/segments/{segment}/data/load"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Then in Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Enter the Marigold hostname used to access the Marigold control panel, this is usually ", _jsx(_components.code, {
            children: "{organization}.slgnt.eu"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Enter the API and Secret keys from the Service Account you created"
        }), "\n", _jsx(_components.li, {
          children: "Enter the organization the lists should be created in"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Marigold Engage destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Marigold Engage destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Field mapping is where you select which customer information parameters or other record parameters you want to send to Marigold.\nYou can map data from any of your model columns to the list's preconfigured fields."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
